import { computed, reactive } from 'vue'

/**
 * Offering to install the PWA (NFR-PWA-01; A-UI-038). Chromium browsers fire
 * `beforeinstallprompt`, which is kept so our own "Install" button can open the real
 * dialog; iOS Safari has no such event, so there we explain Share -> Add to Home Screen.
 * Nothing is offered when the app already runs installed, or for a while after "Not now".
 */
interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISSED_KEY = 'fumbo.install.dismissed-until'
const SNOOZE_DAYS = 14

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}
function isIos(): boolean {
  const ua = navigator.userAgent
  // iPadOS reports itself as a Mac; touch tells them apart
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
}
function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(DISMISSED_KEY) ?? 0) > Date.now()
  } catch {
    return false
  }
}

const state = reactive({
  event: null as InstallPromptEvent | null,
  installed: false,
  snoozed: false,
  ios: false,
})

export const install = {
  /** show the install bar */
  visible: computed(
    () => !state.installed && !state.snoozed && (state.event !== null || state.ios),
  ),
  /** iOS: explain instead of offering a button */
  manual: computed(() => state.event === null && state.ios),
  async accept(): Promise<void> {
    const event = state.event
    if (!event) return
    state.event = null
    await event.prompt()
    if ((await event.userChoice).outcome === 'accepted') state.installed = true
  },
  later(): void {
    state.snoozed = true
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + SNOOZE_DAYS * 86_400_000))
    } catch {
      /* private mode: it simply shows again next visit */
    }
  },
}

/** Call once at start-up: the browser may fire its offer before any screen mounts. */
export function watchForInstall(): void {
  state.installed = isStandalone()
  state.snoozed = snoozed()
  state.ios = isIos() && !state.installed
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault() // we show our own bar instead of the browser's mini-infobar
    state.event = event as InstallPromptEvent
  })
  window.addEventListener('appinstalled', () => {
    state.installed = true
    state.event = null
  })
}
